import { useOutletContext } from 'react-router-dom'
import React, { useEffect, useState } from 'react'
import { useSocketState } from '../../helpers/useSocket'
import { getBackendUrl } from '../../helpers/url'
import { isAuthEnabled, getToken } from '../../helpers/auth'
import { FaExternalLinkAlt, FaCopy, FaCheck } from 'react-icons/fa'

const AOE2CM_URL = process.env.REACT_APP_AOE2CM_URL || 'https://aoe2cm.net'

function applyFormat(format, vars) {
	return format.replace(/\{(\w+)\}/g, (_, key) => vars[key] ?? `{${key}}`)
}

function buildSchedulePreview(match, tournament) {
	const schedule = tournament?.schedule
	const activeDay = schedule?.days?.[schedule?.activeDay]
	const matches = activeDay?.matches ?? []
	const tz = schedule?.timezone || 'UTC'
	const dayDate = activeDay?.date || ''

	const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
	function formatDateLabel(dateStr) {
		if (!dateStr) return ''
		const month = parseInt(dateStr.slice(5, 7), 10) - 1
		const day = dateStr.slice(8, 10)
		return `${MONTHS[month] ?? ''} ${day}`
	}

	const prefix = match?.schedulePrefix || ''
	const format = match?.scheduleFormat || '{startTime} - {leftName} vs {rightName} ({matchType})'
	const divider = match?.scheduleDivider || ' | '

	if (!matches.length) return { preview: prefix || '(no matches scheduled)', matchPreviews: [] }

	const matchPreviews = matches.map((entry) => {
		const entryDate = entry.startTime || dayDate
		const datePart = formatDateLabel(entryDate)
		const timePart = entry.startTimeOfDay || ''
		let startFormatted = [datePart, timePart].filter(Boolean).join(' - ')
		if (timePart) startFormatted += ` ${tz}`

		const vars = {
			matchTitle: entry.matchTitle ?? '',
			startTime: startFormatted,
			startTimeOfDay: timePart,
			timezone: tz,
			leftName: entry.leftPlayer?.name ?? '',
			rightName: entry.rightPlayer?.name ?? '',
			leftScore: entry.finished ? String(entry.leftPlayer?.score ?? '') : '-',
			rightScore: entry.finished ? String(entry.rightPlayer?.score ?? '') : '-',
			matchType: entry.matchTypeDisplay ?? '',
			finished: String(entry.finished ?? false),
		}
		return applyFormat(format, vars)
	})

	const output = (prefix ? prefix + divider : '') + matchPreviews.join(divider)
	return { preview: output, matchPreviews }
}

function ScheduleFormatEditor({ match, tournament, onPrefixChange, onFormatChange, onDividerChange }) {
	const { preview } = buildSchedulePreview(match, tournament)

	return (
		<div className="flex flex-col gap-1">
			<div className="flex flex-row items-center gap-2">
				<label className="font-medium text-gray-900">Schedule Format</label>
				<a href={`${getBackendUrl()}/api/public/scheduleMessage`} target="_blank" rel="noreferrer">
					<button className="bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm">
						Open
						<FaExternalLinkAlt className="text-xs" />
					</button>
				</a>
			</div>
			<div className="flex flex-col gap-2">
				<div className="flex flex-row items-center gap-2">
					<span className="text-sm text-gray-700 w-16">Prefix</span>
					<input
						type="text"
						onChange={onPrefixChange}
						value={match?.schedulePrefix ?? ''}
						placeholder="e.g. Today's matches:"
						className="border rounded px-2 py-1 w-80 font-mono text-sm"
					/>
				</div>
				<div className="flex flex-row items-center gap-2">
					<span className="text-sm text-gray-700 w-16">Match</span>
					<input
						type="text"
						onChange={onFormatChange}
						value={match?.scheduleFormat ?? '{startTime} - {leftName} vs {rightName} ({matchType})'}
						className="border rounded px-2 py-1 w-80 font-mono text-sm"
					/>
				</div>
				<div className="flex flex-row items-center gap-2">
					<span className="text-sm text-gray-700 w-16">Divider</span>
					<input type="text" onChange={onDividerChange} value={match?.scheduleDivider ?? ' | '} className="border rounded px-2 py-1 w-80 font-mono text-sm" />
				</div>
			</div>
			<span className="text-sm text-gray-600 bg-gray-50 ring-1 ring-gray-200 rounded-md px-3 py-1.5 max-w-2xl break-words shadow-sm">{preview}</span>
			<span className="text-xs text-gray-500">
				Available variables: {'{matchTitle}'}, {'{startTime}'}, {'{startTimeOfDay}'}, {'{timezone}'}, {'{leftName}'}, {'{rightName}'}, {'{leftScore}'}, {'{rightScore}'},{' '}
				{'{matchType}'}, {'{finished}'}
			</span>
		</div>
	)
}

function SettingsDashboard() {
	const { socket, config } = useOutletContext()
	//const [config, setConfig] = useState({});

	const [themeOptions, setThemeOptions] = useState([])
	const [aoe2recsCopied, setAoe2recsCopied] = useState(false)
	const [match] = useSocketState('Match')
	const [tournament] = useSocketState('Tournament')
	const [civDraft] = useSocketState('CivDraft')
	const [mapDraft] = useSocketState('MapDraft')
	const [gameCivDraft] = useSocketState('GameCivDraft')

	const matchVars = {
		leftName: match?.leftPlayer?.name || '',
		rightName: match?.rightPlayer?.name || '',
		leftScore: String(match?.leftPlayer?.score ?? ''),
		rightScore: String(match?.rightPlayer?.score ?? ''),
		leftColor: match?.leftPlayer?.color ?? '',
		rightColor: match?.rightPlayer?.color ?? '',
		matchType: match?.matchType ?? '',
		matchTypeDisplay: match?.matchTypeDisplay ?? '',
		matchTypeLength: String(match?.matchTypeLength ?? ''),
		roundInfo: match?.roundInfo ?? '',
		matchStatus: match?.matchStatus ?? '',
		caster1Name: match?.caster1?.name ?? '',
		caster1Country: match?.caster1?.country ?? '',
		caster2Name: match?.caster2?.name ?? '',
		caster2Country: match?.caster2?.country ?? '',
		intervieweeName: match?.interviewee?.name ?? '',
		intervieweeCountry: match?.interviewee?.country ?? '',
	}

	const draftVars = {
		civDraftId: civDraft?.draftId ?? '',
		mapDraftId: mapDraft?.draftId ?? '',
		gameCivDraftId: gameCivDraft?.draftId ?? '',
		civDraftUrl: civDraft?.draftId ? `${AOE2CM_URL}/draft/${civDraft.draftId}` : '',
		mapDraftUrl: mapDraft?.draftId ? `${AOE2CM_URL}/draft/${mapDraft.draftId}` : '',
		gameCivDraftUrl: gameCivDraft?.draftId ? `${AOE2CM_URL}/draft/${gameCivDraft.draftId}` : '',
	}

	function getAoe2recsUrl() {
		const base = `${getBackendUrl()}/api/aoe2recs`
		const token = isAuthEnabled() ? getToken() : null
		return token ? `${base}?token=${token}` : base
	}

	function copyAoe2recsUrl() {
		navigator.clipboard.writeText(getAoe2recsUrl())
		setAoe2recsCopied(true)
		setTimeout(() => setAoe2recsCopied(false), 2000)
	}

	function updateTheme(data) {
		socket.emit('updateTheme', { ...config, ...data })
	}

	function updateBracketProvider(data) {
		socket.emit('updateBracketProvider', data.bracketProvider)
	}

	function updateH2HProvider(data) {
		socket.emit('updateH2HProvider', data.h2hProvider)
	}

	function setAutoFetchInterval(event) {
		socket.emit('updateAutoFetchInterval', Number(event.target.value))
	}

	function toggleIgnorePlayerNames() {
		socket.emit('modifyMatch', {
			ignorePlayerNames: !match.ignorePlayerNames,
		})
	}

	function toggleCalculateScoreFromGames() {
		socket.emit('modifyMatch', {
			calculateScoreFromGames: !match.calculateScoreFromGames,
		})
	}

	function toggleFilterCivs() {
		socket.emit('modifyMatch', {
			filterCivs: !match.filterCivs,
		})
	}

	function setSheepOMeterAutotoggle(event) {
		socket.emit('modifyMatch', {
			sheepOMeterAutotoggle: event.target.value,
		})
	}

	function setMinimapAutotoggle(event) {
		socket.emit('modifyMatch', { minimapAutotoggle: event.target.value })
	}

	function setScoreMessageFormat(event) {
		socket.emit('modifyMatch', { scoreMessageFormat: event.target.value })
	}

	function setCastersFormat(event) {
		socket.emit('modifyMatch', { castersFormat: event.target.value })
	}

	function setDraftFormat(event) {
		socket.emit('modifyMatch', { draftFormat: event.target.value })
	}

	function setSchedulePrefix(event) {
		socket.emit('modifyMatch', { schedulePrefix: event.target.value })
	}

	function setScheduleFormat(event) {
		socket.emit('modifyMatch', { scheduleFormat: event.target.value })
	}

	function setScheduleDivider(event) {
		socket.emit('modifyMatch', { scheduleDivider: event.target.value })
	}

	useEffect(() => {
		socket.emit('registerSettings')

		const host = process.env.NODE_ENV === 'production' ? window.location.host : `${window.location.hostname}:3001`

		fetch(`${window.location.protocol}//${host}/api/themes`)
			.then((response) => response.json())
			.then((data) => {
				setThemeOptions(data)
			})
	}, [])

	return (
		<div>
			<h1 className="font-bold text-lg">Live Game Overlay Settings</h1>
			<div className="flex flex-col gap-4">
				<label className="inline-flex items-center cursor-pointer">
					<span className="ms-3 font-medium text-gray-900 mr-2">Ignore Player Names</span>
					<input type="checkbox" checked={match?.ignorePlayerNames} className="sr-only peer" onChange={toggleIgnorePlayerNames} />
					<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
				</label>

				<label className="inline-flex items-center cursor-pointer">
					<span className="ms-3 font-medium text-gray-900 mr-2">Calculate score from matches</span>
					<input type="checkbox" checked={match?.calculateScoreFromGames} className="sr-only peer" onChange={toggleCalculateScoreFromGames} />
					<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
				</label>

				<label className="inline-flex items-center cursor-pointer">
					<span className="ms-3 font-medium text-gray-900 mr-2">Filter civs (from draft)</span>
					<input type="checkbox" checked={match?.filterCivs} className="sr-only peer" onChange={toggleFilterCivs} />
					<div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
				</label>

				<label className="inline-flex items-center cursor-pointer">
					<span className="ms-3 font-medium text-gray-900 mr-2">Turn off sheep-o-meter after</span>
					<input type="number" onChange={setSheepOMeterAutotoggle} value={match?.sheepOMeterAutotoggle || -1} id="sheep-o-meter" min="0" name="sheep-o-meter" />
					<span className="ms-3 font-medium text-gray-900 mr-2">sec ingame</span>
				</label>

				<label className="inline-flex items-center cursor-pointer">
					<span className="ms-3 font-medium text-gray-900 mr-2">Turn off minimap after</span>
					<input type="number" onChange={setMinimapAutotoggle} value={match?.minimapAutotoggle || -1} id="sheep-o-meter" min="0" name="sheep-o-meter" />
					<span className="ms-3 font-medium text-gray-900 mr-2">sec ingame</span>
				</label>

				<div className="flex flex-col gap-1">
					<div className="flex flex-row items-center gap-2">
						<label className="font-medium text-gray-900">Score Message Format</label>
						<a href={`${getBackendUrl()}/api/public/scoreMessage`} target="_blank" rel="noreferrer">
							<button className="bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm">
								Open
								<FaExternalLinkAlt className="text-xs" />
							</button>
						</a>
					</div>
					<div className="flex flex-row items-center gap-2">
						<input
							type="text"
							onChange={setScoreMessageFormat}
							value={match?.scoreMessageFormat ?? '{leftName} {leftScore}-{rightScore} vs {rightName} ({matchTypeDisplay})'}
							className="border rounded px-2 py-1 w-96 font-mono text-sm"
						/>
						<span className="text-sm text-gray-600 bg-gray-50 ring-1 ring-gray-200 rounded-md ml-8 px-3 py-1.5 max-w-xl truncate shadow-sm">
							{applyFormat(match?.scoreMessageFormat ?? '{leftName} {leftScore}-{rightScore} vs {rightName} ({matchTypeDisplay})', matchVars)}
						</span>
					</div>
					<span className="text-xs text-gray-500">
						Available variables: {'{leftName}'}, {'{rightName}'}, {'{leftScore}'}, {'{rightScore}'}, {'{matchType}'}, {'{matchTypeDisplay}'}, {'{matchTypeLength}'},{' '}
						{'{roundInfo}'}, {'{matchStatus}'}, {'{leftColor}'}, {'{rightColor}'}, {'{caster1Name}'}, {'{caster1Country}'}, {'{caster2Name}'}, {'{caster2Country}'},{' '}
						{'{intervieweeName}'}, {'{intervieweeCountry}'}
					</span>
				</div>

				<div className="flex flex-col gap-1">
					<div className="flex flex-row items-center gap-2">
						<label className="font-medium text-gray-900">Casters Format</label>
						<a href={`${getBackendUrl()}/api/public/casters`} target="_blank" rel="noreferrer">
							<button className="bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm">
								Open
								<FaExternalLinkAlt className="text-xs" />
							</button>
						</a>
					</div>
					<div className="flex flex-row items-center gap-2">
						<input
							type="text"
							onChange={setCastersFormat}
							value={match?.castersFormat ?? '{caster1Name} & {caster2Name}'}
							className="border rounded px-2 py-1 w-96 font-mono text-sm"
						/>
						<span className="text-sm text-gray-600 bg-gray-50 ring-1 ring-gray-200 rounded-md ml-8 px-3 py-1.5 max-w-xl truncate shadow-sm">
							{applyFormat(match?.castersFormat ?? '{caster1Name} & {caster2Name}', matchVars)}
						</span>
					</div>
					<span className="text-xs text-gray-500">
						Available variables: {'{caster1Name}'}, {'{caster1Country}'}, {'{caster2Name}'}, {'{caster2Country}'}, {'{intervieweeName}'}, {'{intervieweeCountry}'}
					</span>
				</div>

				<div className="flex flex-col gap-1">
					<div className="flex flex-row items-center gap-2">
						<label className="font-medium text-gray-900">Draft Format</label>
						<a href={`${getBackendUrl()}/api/public/draftMessage`} target="_blank" rel="noreferrer">
							<button className="bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm">
								Open
								<FaExternalLinkAlt className="text-xs" />
							</button>
						</a>
					</div>
					<div className="flex flex-row items-center gap-2">
						<input
							type="text"
							onChange={setDraftFormat}
							value={match?.draftFormat ?? 'Civ Draft: {civDraftUrl} | Map Draft: {mapDraftUrl}'}
							className="border rounded px-2 py-1 w-96 font-mono text-sm"
						/>
						<span className="text-sm text-gray-600 bg-gray-50 ring-1 ring-gray-200 rounded-md ml-8 px-3 py-1.5 max-w-xl truncate shadow-sm">
							{applyFormat(match?.draftFormat ?? 'Civ Draft: {civDraftUrl} | Map Draft: {mapDraftUrl}', { ...matchVars, ...draftVars })}
						</span>
					</div>
					<span className="text-xs text-gray-500">
						Available variables: {'{civDraftUrl}'}, {'{mapDraftUrl}'}, {'{gameCivDraftUrl}'}, {'{civDraftId}'}, {'{mapDraftId}'}, {'{gameCivDraftId}'}, {'{leftName}'},{' '}
						{'{rightName}'}, {'{leftScore}'}, {'{rightScore}'}, {'{matchTypeDisplay}'}, {'{roundInfo}'}
					</span>
				</div>
				<ScheduleFormatEditor
					match={match}
					tournament={tournament}
					onPrefixChange={setSchedulePrefix}
					onFormatChange={setScheduleFormat}
					onDividerChange={setScheduleDivider}
				/>
			</div>
			<h1 className="font-bold text-lg mt-4">Integrations</h1>
			<div className="flex flex-col gap-2">
				<div className="flex flex-row items-center gap-2">
					<label className="font-medium text-gray-900">aoe2recs API Endpoint</label>
					<button onClick={copyAoe2recsUrl} className="bg-teal-700 hover:bg-teal-800 rounded flex items-center gap-1 px-2 py-0.5 text-sm">
						{aoe2recsCopied ? <FaCheck className="text-xs" /> : <FaCopy className="text-xs" />}
						{aoe2recsCopied ? 'Copied!' : 'Copy URL'}
					</button>
				</div>
				<span className="text-sm text-gray-600 bg-gray-50 ring-1 ring-gray-200 rounded-md px-3 py-1.5 font-mono max-w-xl shadow-sm break-all blur-sm hover:blur-none transition-all duration-200 cursor-pointer select-none hover:select-text">
					{getAoe2recsUrl()}
				</span>
			</div>
			<h1 className="font-bold text-lg">Themes</h1>
			<select value={config.theme} onChange={(e) => updateTheme({ theme: e.target.value })} id="theme" name="theme">
				{themeOptions.map((theme) => (
					<option key={theme.filename} value={theme.filename}>
						{theme.name}
					</option>
				))}
			</select>
			<h1 className="font-bold text-lg">Bracket Provider</h1>
			<select value={tournament?.bracketProvider} onChange={(e) => updateBracketProvider({ bracketProvider: e.target.value })} id="bracketProvider" name="bracketProvider">
				<option value={'startgg'}>Start.GG</option>
				<option value={'tourneybot'}>Tourney Bot</option>
				<option value={'onimaru'}>Onimaru</option>
			</select>
			{tournament?.bracketProvider === 'startgg' && (
				<label className="flex items-center cursor-pointer mt-2">
					<span className="ms-3 font-medium text-gray-900 mr-2">Auto-fetch ongoing phases every</span>
					<input type="number" onChange={setAutoFetchInterval} value={tournament?.autoFetchInterval || 0} min="0" step="5" name="auto-fetch-interval" />
					<span className="ms-3 font-medium text-gray-900 mr-2">sec (0 = disabled)</span>
				</label>
			)}
			<h1 className="font-bold text-lg">H2H Provider</h1>
			<select value={tournament?.h2hProvider} onChange={(e) => updateH2HProvider({ h2hProvider: e.target.value })} id="h2hProvider" name="h2hProvider">
				<option value={'liquipedia'}>Liquipedia</option>
				<option value={'aoe4world'}>AoE4world</option>
				<option value={'aomstats'}>AoM Stats</option>
			</select>
		</div>
	)
}

export default SettingsDashboard
